import { type PhoebeAgentRunPendingEventRead } from '@apps/web/components/phoebe_chat/streaming_types';
import { type components } from '@libraries/typescript/api_client';

export type RunWatch = components['schemas']['PhoebeAgentRunWatchRead'];

export type ScheduledCheckInRow = {
    readonly kind: 'check_in';
    readonly id: string;
    readonly reason: string | null;
    readonly visibleAt: string;
};

export type WatchRow = {
    readonly kind: 'watch';
    readonly id: string;
    readonly reason: string;
    readonly suspended: boolean;
    readonly expiresAt: string;
    readonly checkEveryMinutes: number;
};

export type BackgroundWorkRow = ScheduledCheckInRow | WatchRow;

const TERMINAL_RUN_STATUSES: ReadonlySet<string> = new Set([
    'cancelled',
    'completed',
    'failed',
]);

function getOptionalString(value: unknown): string | null {
    return typeof value === 'string' && value.trim().length > 0
        ? value.trim()
        : null;
}

/**
 * Selects every live background commitment for the tray: pending future poke
 * fires (earliest first), then watches with active coverage before suspended
 * coverage. Terminal runs report nothing because stale feed pages can still
 * show revoked pokes and watches as live. Suspended watches are intentionally
 * returned after active watches so the UI can bound the visible list without
 * hiding current work.
 */
export function selectBackgroundWork({
    pendingEvents,
    watches,
    runStatus,
    nowMs,
}: {
    readonly pendingEvents: ReadonlyArray<PhoebeAgentRunPendingEventRead>;
    readonly watches: ReadonlyArray<RunWatch>;
    readonly runStatus: string | null;
    readonly nowMs: number;
}): ReadonlyArray<BackgroundWorkRow> {
    if (runStatus !== null && TERMINAL_RUN_STATUSES.has(runStatus)) {
        return [];
    }

    const checkInRows: ReadonlyArray<ScheduledCheckInRow> = pendingEvents
        .filter(
            (pendingEvent) =>
                pendingEvent.type === 'poke' &&
                pendingEvent.status === 'pending',
        )
        .map((pendingEvent) => ({
            id: pendingEvent.id,
            reason: getOptionalString(pendingEvent.payload['reason']),
            visibleAt: pendingEvent.visible_at,
            visibleAtMs: Date.parse(pendingEvent.visible_at),
        }))
        .filter(
            (fire) =>
                Number.isFinite(fire.visibleAtMs) && fire.visibleAtMs > nowMs,
        )
        .sort((left, right) => left.visibleAtMs - right.visibleAtMs)
        .map((fire) => ({
            kind: 'check_in',
            id: fire.id,
            reason: fire.reason,
            visibleAt: fire.visibleAt,
        }));

    const watchRows: ReadonlyArray<WatchRow> = [...watches]
        .filter(
            (watch) =>
                watch.status === 'active' || watch.status === 'suspended',
        )
        .sort((left, right) => {
            if (left.status !== right.status) {
                return left.status === 'active' ? -1 : 1;
            }
            return Date.parse(left.created_at) - Date.parse(right.created_at);
        })
        .map((watch) => ({
            kind: 'watch',
            id: watch.id,
            reason: watch.reason,
            suspended: watch.status === 'suspended',
            expiresAt: watch.expires_at,
            checkEveryMinutes: watch.check_every_minutes,
        }));

    return [...checkInRows, ...watchRows];
}
